import { z } from 'zod';

/**
 * Environment variable schema with runtime validation
 * This ensures all required configuration is present and valid at startup
 */
const envSchema = z.object({
  // API Configuration (REQUIRED)
  VITE_API_BASE_URL: z.string().url('VITE_API_BASE_URL must be a valid URL'),
  VITE_ENVIRONMENT: z
    .enum(['local', 'demo', 'development', 'staging', 'production'])
    .default('local'),

  // Authentication Settings (OPTIONAL - depends on mode)
  VITE_DEMO_MODE: z
    .string()
    .default('false')
    .transform((val) => val === 'true'),
  VITE_LOCAL_DEV: z
    .string()
    .default('false')
    .transform((val) => val === 'true'),
  VITE_SKIP_AUTH: z
    .string()
    .default('false')
    .transform((val) => val === 'true'),

  // Azure AD Configuration (OPTIONAL - required only if auth enabled)
  VITE_AZURE_CLIENT_ID: z.string().optional(),
  VITE_AZURE_TENANT_ID: z.string().optional(),
  VITE_AZURE_REDIRECT_URI: z.string().url().optional(),

  // Security Settings
  VITE_ENFORCE_HTTPS: z
    .string()
    .default('false')
    .transform((val) => val === 'true'),

  // External Services
  VITE_CONTACT_URL: z.string().url().default('https://goviq.us/#contact'),
  VITE_SUPPORT_EMAIL: z.string().email().default('contact@goviq.us'),
  VITE_DOCUMENTATION_URL: z.string().url().default('https://docs.goviq.us'),

  // Demo login hint — email pre-filled on the Microsoft login screen when ?demo=true
  // Required only in production when the demo URL is used; leave blank in development
  VITE_DEMO_LOGIN_HINT: z.string().email().optional(),

  // Demo password hint — displayed on the demo landing page so prospects know what to type
  // Set in production via GitHub secret VITE_DEMO_PASSWORD_HINT; leave blank in development
  VITE_DEMO_PASSWORD_HINT: z.string().optional(),

  // Feature Flags
  VITE_ENABLE_CHATBOT: z
    .string()
    .default('true')
    .transform((val) => val === 'true'),
  VITE_ENABLE_ANALYTICS: z
    .string()
    .default('true')
    .transform((val) => val === 'true'),

  // Logging
  VITE_LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).default('info'),
  VITE_ENABLE_API_LOGGING: z
    .string()
    .default('false')
    .transform((val) => val === 'true'),
  VITE_ENABLE_PERFORMANCE_MONITORING: z
    .string()
    .default('false')
    .transform((val) => val === 'true'),
});

/**
 * Validated environment variables
 * This object is guaranteed to have all required configuration or will fail at startup
 */
export type Env = z.infer<typeof envSchema>;

/**
 * Validation result type
 */
export interface ValidationResult {
  success: boolean;
  data?: Env;
  errors?: string[];
}

/**
 * Validate environment variables and return result
 * This function is called at startup to ensure configuration is valid
 */
export function validateEnv(): ValidationResult {
  try {
    const result = envSchema.safeParse(import.meta.env);

    if (!result.success) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errors = result.error.issues.map((err: any) => {
        const path = err.path.join('.');
        return `${path}: ${err.message}`;
      });

      console.error('❌ ENVIRONMENT VALIDATION FAILED');
      console.error('Missing or invalid environment variables:');
      errors.forEach((error: string, index: number) => {
        console.error(`  ${index + 1}. ${error}`);
      });

      return {
        success: false,
        errors,
      };
    }

    // Additional validation: If auth is enabled, Azure AD config must be present
    if (!result.data.VITE_SKIP_AUTH && !result.data.VITE_DEMO_MODE) {
      const missingAuthConfig: string[] = [];

      if (!result.data.VITE_AZURE_CLIENT_ID) {
        missingAuthConfig.push('VITE_AZURE_CLIENT_ID is required when authentication is enabled');
      }
      if (!result.data.VITE_AZURE_TENANT_ID) {
        missingAuthConfig.push('VITE_AZURE_TENANT_ID is required when authentication is enabled');
      }

      if (missingAuthConfig.length > 0) {
        console.error('❌ AUTHENTICATION CONFIGURATION INCOMPLETE');
        missingAuthConfig.forEach((error: string, index: number) => {
          console.error(`  ${index + 1}. ${error}`);
        });

        return {
          success: false,
          errors: missingAuthConfig,
        };
      }
    }

    // eslint-disable-next-line no-console
    console.log('✅ Environment validation passed');
    // eslint-disable-next-line no-console
    console.log(`✅ Environment: ${result.data.VITE_ENVIRONMENT}`);
    // eslint-disable-next-line no-console
    console.log(`✅ API Base URL: ${result.data.VITE_API_BASE_URL}`);

    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    console.error('❌ Unexpected error during environment validation:', error);
    return {
      success: false,
      errors: ['Unexpected validation error'],
    };
  }
}

/**
 * Get validated environment variables
 * Throws if validation fails - use this for fail-fast behavior
 */
export function getEnv(): Env {
  const result = validateEnv();

  if (!result.success) {
    throw new Error('Environment validation failed. Check console for details.');
  }

  return result.data!;
}

/**
 * Validated environment variables (singleton)
 * This is the ONLY place in the codebase that should access import.meta.env
 */
export const env = getEnv();
